import { z } from 'zod';
import { auditFieldsSchema, cepSchema, emailSchema, idSchema, isoDateSchema, paginated, paginationQuerySchema, phoneSchema, ufSchema, } from './common.js';
import { documentSchema } from './documents.js';
/**
 * Pessoa unificada.
 *
 * Decisão do núcleo: cliente, fornecedor e colaborador NÃO são tabelas
 * diferentes — são PAPÉIS da mesma Pessoa. Um fornecedor que também é
 * prestador e cliente é um registro só, com três papéis.
 */
export const PERSON_KINDS = ['FISICA', 'JURIDICA'];
export const personKindSchema = z.enum(PERSON_KINDS);
export const PERSON_KIND_LABELS = {
    FISICA: 'Pessoa física',
    JURIDICA: 'Pessoa jurídica',
};
export const PERSON_ROLES = [
    'FORNECEDOR',
    'CLIENTE',
    'COLABORADOR',
    'TRANSPORTADOR',
    'PARCEIRO',
];
export const personRoleSchema = z.enum(PERSON_ROLES);
export const PERSON_ROLE_LABELS = {
    FORNECEDOR: 'Fornecedor',
    CLIENTE: 'Cliente',
    COLABORADOR: 'Colaborador',
    TRANSPORTADOR: 'Transportador',
    PARCEIRO: 'Parceiro',
};
/** Tipos de chave PIX aceitos pelo Banco Central. */
export const PIX_KEY_KINDS = ['CPF', 'CNPJ', 'EMAIL', 'TELEFONE', 'ALEATORIA'];
export const pixKeyKindSchema = z.enum(PIX_KEY_KINDS);
export const PIX_KEY_KIND_LABELS = {
    CPF: 'CPF',
    CNPJ: 'CNPJ',
    EMAIL: 'E-mail',
    TELEFONE: 'Telefone',
    ALEATORIA: 'Chave aleatória',
};
export const personSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    kind: personKindSchema,
    /** Razão social (PJ) ou nome completo (PF). */
    name: z.string(),
    /** Nome fantasia (PJ) ou apelido (PF). */
    tradeName: z.string().nullable(),
    /** Somente dígitos: CPF (11) ou CNPJ (14). */
    document: z.string().nullable(),
    stateRegistration: z.string().nullable(),
    municipalRegistration: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    whatsapp: z.string().nullable(),
    contactName: z.string().nullable(),
    birthDate: z.string().nullable(),
    zip: z.string().nullable(),
    street: z.string().nullable(),
    number: z.string().nullable(),
    complement: z.string().nullable(),
    district: z.string().nullable(),
    city: z.string().nullable(),
    uf: z.string().nullable(),
    ibgeCode: z.string().nullable(),
    bankName: z.string().nullable(),
    bankAgency: z.string().nullable(),
    bankAccount: z.string().nullable(),
    pixKey: z.string().nullable(),
    pixKeyKind: pixKeyKindSchema.nullable(),
    notes: z.string().nullable(),
    roles: z.array(personRoleSchema),
    isActive: z.boolean(),
});
/**
 * Campos crus da pessoa. Fica separado das regras de coerência para que o PATCH
 * possa reutilizar a mesma forma via `.partial()` — Zod não permite `.partial()`
 * sobre um schema já refinado.
 */
export const personFieldsSchema = z.object({
    kind: personKindSchema,
    name: z.string().trim().min(2, 'Informe o nome ou a razão social').max(180),
    tradeName: z.string().trim().max(180).optional().nullable(),
    document: documentSchema.optional().nullable(),
    stateRegistration: z.string().trim().max(40).optional().nullable(),
    municipalRegistration: z.string().trim().max(40).optional().nullable(),
    email: emailSchema.optional().nullable(),
    phone: phoneSchema.optional().nullable(),
    whatsapp: phoneSchema.optional().nullable(),
    contactName: z.string().trim().max(120).optional().nullable(),
    birthDate: isoDateSchema.optional().nullable(),
    zip: cepSchema.optional().nullable(),
    street: z.string().trim().max(180).optional().nullable(),
    number: z.string().trim().max(20).optional().nullable(),
    complement: z.string().trim().max(120).optional().nullable(),
    district: z.string().trim().max(120).optional().nullable(),
    city: z.string().trim().max(120).optional().nullable(),
    uf: ufSchema.optional().nullable(),
    ibgeCode: z.string().trim().max(10).optional().nullable(),
    bankName: z.string().trim().max(80).optional().nullable(),
    bankAgency: z.string().trim().max(20).optional().nullable(),
    bankAccount: z.string().trim().max(30).optional().nullable(),
    pixKey: z.string().trim().max(140).optional().nullable(),
    pixKeyKind: pixKeyKindSchema.optional().nullable(),
    notes: z.string().trim().max(2000).optional().nullable(),
    roles: z.array(personRoleSchema).min(1, 'Selecione ao menos um papel'),
    isActive: z.boolean().default(true),
});
/** CPF tem 11 dígitos e é de pessoa física; CNPJ tem 14 e é de jurídica. */
function checkDocumentMatchesKind(value, ctx) {
    if (!value.document || !value.kind)
        return;
    if (value.kind === 'JURIDICA' && value.document.length !== 14) {
        ctx.addIssue({ code: 'custom', path: ['document'], message: 'Pessoa jurídica exige CNPJ' });
    }
    if (value.kind === 'FISICA' && value.document.length !== 11) {
        ctx.addIssue({ code: 'custom', path: ['document'], message: 'Pessoa física exige CPF' });
    }
}
export const personInputSchema = personFieldsSchema.superRefine(checkDocumentMatchesKind);
/** PATCH: todo campo é opcional, mas a coerência CPF/CNPJ continua valendo. */
export const personUpdateSchema = personFieldsSchema.partial().superRefine(checkDocumentMatchesKind);
export const personListQuerySchema = paginationQuerySchema.extend({
    role: personRoleSchema.optional(),
    kind: personKindSchema.optional(),
    uf: z.string().trim().length(2).toUpperCase().optional(),
    isActive: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const personListResponseSchema = paginated(personSchema);
//# sourceMappingURL=person.js.map